"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "./ReferralTree.css";
import { Context } from "../../../../utils/context";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const initial = (name) => (name || "?").trim().charAt(0).toUpperCase();

function MemberCard({ member, isRoot, expanded, loading, onToggle, onSelect }) {
  const active = !!member.affiliate_status;
  const canExpand = member.directs > 0;

  return (
    <div className="rt_node">
      <div
        className={`rt_card ${isRoot ? "rt_card_root" : ""} ${active ? "" : "rt_card_off"}`}
        onClick={() => onSelect(member)}
      >
        <span className={`rt_tag ${isRoot ? "rt_tag_you" : ""}`}>
          {isRoot ? "CUSTOMER" : active ? "ACTIVE" : "INACTIVE"}
        </span>
        <div className="rt_avatar">{initial(member.name)}</div>
        <div className="rt_name">{member.name}</div>
        <div className="rt_uid">{member.user_uid}</div>
        <div className="rt_status">
          <i className={`rt_dot ${active ? "on" : "off"}`} />
          {active ? "Active" : "Inactive"}
        </div>
        <div className="rt_meta">
          {member.directs} directs · {member.team} team
        </div>
      </div>
      {canExpand && (
        <button
          type="button"
          className="rt_toggle"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(member);
          }}
        >
          {loading ? "…" : expanded ? "−" : "+"}
        </button>
      )}
    </div>
  );
}

function TreeBranch({
  member,
  isRoot,
  cache,
  expandedIds,
  loadingIds,
  onToggle,
  onSelect,
}) {
  const children = cache[member.id] || [];
  const expanded = expandedIds.has(member.id);

  return (
    <li>
      <MemberCard
        member={member}
        isRoot={isRoot}
        expanded={expanded}
        loading={loadingIds.has(member.id)}
        onToggle={onToggle}
        onSelect={onSelect}
      />
      {expanded && children.length > 0 && (
        <ul>
          {children.map((c) => (
            <TreeBranch
              key={c.id}
              member={c}
              isRoot={false}
              cache={cache}
              expandedIds={expandedIds}
              loadingIds={loadingIds}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function ReferralTree({ customerId }) {
  const { getData } = useContext(Context);
  const [root, setRoot] = useState(null);
  const [cache, setCache] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [trail, setTrail] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const members = useRef({});

  const fetchNode = useCallback(
    async (id) => {
      const res = await getData(
        `/admin/customer/referral-tree/${customerId}${id ? `?user_id=${id}` : ""}`,
      );
      return res?.data || null;
    },
    [customerId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRoot = useCallback(async () => {
    setLoading(true);
    const data = await fetchNode();
    if (data?.node) {
      members.current = {};
      [data.node, ...data.children].forEach((m) => (members.current[m.id] = m));
      setRoot(data.node);
      setTrail([data.node]);
      setCache({ [data.node.id]: data.children });
      setExpandedIds(new Set(data.children.length ? [data.node.id] : []));
    }
    setLoading(false);
  }, [fetchNode]);

  useEffect(() => {
    loadRoot();
  }, [loadRoot]);

  const toggle = async (member) => {
    if (expandedIds.has(member.id)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
      return;
    }
    if (!cache[member.id]) {
      setLoadingIds((prev) => new Set(prev).add(member.id));
      const data = await fetchNode(member.id);
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
      if (!data) return;
      data.children.forEach((m) => (members.current[m.id] = m));
      setCache((prev) => ({ ...prev, [member.id]: data.children }));
    }
    setExpandedIds((prev) => new Set(prev).add(member.id));
  };

  // Expand every loaded node whose children are not loaded yet, level by level
  const expandAll = async () => {
    let frontier = Object.values(members.current).filter((m) => m.directs > 0);
    const nextCache = { ...cache };
    const nextExpanded = new Set(expandedIds);
    let guard = 0;
    while (frontier.length && guard++ < 6) {
      const toLoad = frontier.filter((m) => !nextCache[m.id]);
      const results = await Promise.all(toLoad.map((m) => fetchNode(m.id)));
      const found = [];
      toLoad.forEach((m, i) => {
        const kids = results[i]?.children || [];
        nextCache[m.id] = kids;
        kids.forEach((k) => {
          members.current[k.id] = k;
          if (k.directs > 0) found.push(k);
        });
      });
      frontier.forEach((m) => nextExpanded.add(m.id));
      frontier = found;
    }
    setCache(nextCache);
    setExpandedIds(nextExpanded);
  };

  const focus = trail[trail.length - 1] || root;

  const collapseAll = () => setExpandedIds(new Set(focus ? [focus.id] : []));

  const reset = () => {
    setZoom(1);
    setSearch("");
    setSuggestions([]);
    setSelected(null);
    loadRoot();
  };

  // Make a member the top of the tree; trail is the breadcrumb path.
  const focusOn = async (member, newTrail) => {
    let data = null;
    if (!cache[member.id]) {
      data = await fetchNode(member.id);
      if (!data) return;
      members.current[member.id] = data.node;
      data.children.forEach((m) => (members.current[m.id] = m));
      setCache((prev) => ({ ...prev, [member.id]: data.children }));
    }
    const fresh = data?.node || member;
    setTrail(newTrail.map((m) => (m.id === member.id ? fresh : m)));
    setExpandedIds((prev) => new Set(prev).add(member.id));
    setSelected(null);
  };

  const showMemberTree = (member) => {
    const idx = trail.findIndex((m) => m.id === member.id);
    focusOn(member, idx >= 0 ? trail.slice(0, idx + 1) : [...trail, member]);
  };

  const pickSuggestion = (u) => {
    setSearch("");
    setSuggestions([]);
    // exact path is unknown for a search hit: root > member
    focusOn(u, root.id === u.id ? [root] : [root, u]);
  };

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const res = await getData(
        `/admin/customer/referral-tree-search/${customerId}?q=${encodeURIComponent(q)}`,
      );
      setSuggestions(res?.data?.users || []);
    }, 300);
    return () => clearTimeout(t);
  }, [search, customerId]);

  const onSearch = (e) => {
    e.preventDefault();
    if (suggestions[0]) pickSuggestion(suggestions[0]);
  };

  return (
    <div className="rt_wrap">
      <div className="rt_toolbar">
        <div className="rt_group">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(1)))}
          >
            −
          </button>
          <span className="rt_zoom">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(1)))}
          >
            +
          </button>
        </div>
        <button type="button" onClick={expandAll}>
          Expand
        </button>
        <button type="button" onClick={collapseAll}>
          Collapse
        </button>
        <button type="button" onClick={reset}>
          Reset
        </button>
        <form className="rt_search" onSubmit={onSearch}>
          <input
            type="text"
            placeholder="Search member ID or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {suggestions.length > 0 && (
            <ul className="rt_suggest">
              {suggestions.map((u) => (
                <li key={u.id} onClick={() => pickSuggestion(u)}>
                  <i
                    className={`rt_dot ${u.affiliate_status ? "on" : "off"}`}
                  />
                  <strong>{u.name}</strong>
                  <span className="rt_uid">{u.user_uid}</span>
                </li>
              ))}
            </ul>
          )}
        </form>
      </div>
      <p className="rt_hint">
        Tap a member to see details and bonus earned. Use the + buttons to open
        deeper levels.
      </p>
      {trail.length > 1 && (
        <nav className="rt_crumbs">
          {trail.map((m, i) => (
            <React.Fragment key={m.id}>
              {i > 0 && <span className="rt_sep">›</span>}
              {i === trail.length - 1 ? (
                <b>
                  {m.name} <small className="rt_uid">{m.user_uid}</small>
                </b>
              ) : (
                <button type="button" onClick={() => showMemberTree(m)}>
                  {m.name}
                </button>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="rt_canvas">
        {loading ? (
          <p className="rt_empty">Loading network…</p>
        ) : !root ? (
          <p className="rt_empty">Unable to load your network.</p>
        ) : (
          <div className="rt_tree" style={{ transform: `scale(${zoom})` }}>
            <ul>
              <TreeBranch
                member={focus}
                isRoot={focus.id === root.id}
                cache={cache}
                expandedIds={expandedIds}
                loadingIds={loadingIds}
                onToggle={toggle}
                onSelect={setSelected}
              />
            </ul>
            {focus.directs === 0 && (
              <p className="rt_empty">
                {focus.id === root.id
                  ? "This customer has not referred anyone yet."
                  : "This member has no referrals yet."}
              </p>
            )}
          </div>
        )}
      </div>

      {selected && (
        <>
          <div className="rt_backdrop" onClick={() => setSelected(null)} />
          <aside className="rt_drawer">
            <div className="rt_drawer_head">
              <h4>Member details</h4>
              <button type="button" onClick={() => setSelected(null)}>
                ×
              </button>
            </div>
            <div className="rt_drawer_id">
              <div className="rt_avatar">{initial(selected.name)}</div>
              <div>
                <strong>{selected.name}</strong>
                <div className="rt_uid">{selected.user_uid}</div>
                <span
                  className={`rt_pill ${selected.affiliate_status ? "on" : "off"}`}
                >
                  {selected.affiliate_status ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            {selected.id !== root?.id && (
              <div className="rt_bonus">
                <span>Bonus earned by customer from this member</span>
                <strong>{inr(selected.bonus_total)}</strong>
                <div className="rt_bonus_split">
                  {/* <span>Joining {inr(selected.bonus_joining)}</span> */}
                  {/* <span>Repurchase {inr(selected.bonus_repurchase)}</span> */}
                </div>
              </div>
            )}
            <dl className="rt_dl">
              <dt>Direct members</dt>
              <dd>{selected.directs}</dd>
              <dt>Total team</dt>
              <dd>{selected.team}</dd>
              {selected.affiliate_status && selected.refer_code && (
                <>
                  <dt>Referral code</dt>
                  <dd>{selected.refer_code}</dd>
                </>
              )}
              <dt>Joined</dt>
              <dd>
                {selected.createdAt
                  ? new Date(selected.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "-"}
              </dd>
            </dl>
            {selected?.affiliate_status && selected.id !== focus?.id && (
              <button
                type="button"
                className="rt_primary"
                onClick={() => showMemberTree(selected)}
              >
                Show this member's tree
              </button>
            )}
          </aside>
        </>
      )}
    </div>
  );
}

export default ReferralTree;
